/**
 * resolveDropStepConfig
 *
 * Resolves the DropVideoStep's per-flow `accept` / `title` / `description`
 * overrides for the AddContent sheet. Extracted from `AddContentSheetContent`
 * to flatten the nested per-flow ternaries and keep the component under the
 * size ceiling (finding_4). Pure — behaviour-identical to the inline block it
 * replaced.
 *
 * Five flows:
 * - community → images + videos with the image-or-video copy (the go-live
 *   switch, this story). Videos use the FULL SUPPORTED_VIDEO_ACCEPT list
 *   (chat-equal) and take the new Google-Transcoder pipeline; the IMAGE half
 *   KEEPS the NARROWER community accept list (no HEIC/HEIF): a community image
 *   still raw-uploads to the frozen ffmpeg image CF, which can't decode
 *   HEIC/HEIF → a stuck "Processing" message (finding_1). `{max}` is
 *   CHAT_CONTENT_MAX_SECONDS ceiled to minutes (= 5) — the chat-equal 5-minute
 *   video limit (task-prompt line 2).
 * - chat → images + videos with the image-or-video copy. Keeps the FULL image
 *   list — its client-transcode path re-encodes to JPEG before upload. `{max}`
 *   is CHAT_CONTENT_MAX_SECONDS ceiled to minutes (= 5).
 * - feedback → IMAGE-ONLY (Flutter in_app_feedback_screen.dart:73-81 picks
 *   gallery images only, never video), with the image-only accept list and
 *   image-drop copy. Uses the NARROWER community accept list (no HEIC/HEIF):
 *   feedback raw-uploads the source file with no client-side re-encode and no
 *   transcode CF, and the web read side renders screenshots in a plain `<img>`
 *   which can't decode HEIC/HEIF on Chrome/Firefox → broken image (finding_1).
 * - profileImage → IMAGE-ONLY, sharing the feedback branch's identical config
 *   (Flutter edit_user_profile_screen.dart:270-286 picks with `allowVideo: false`
 *   + `allowAudio: false`). Also uses the NARROWER community accept list, but for
 *   its own reason: the profile path re-encodes through
 *   `memoryStorage.compressImage`, a `<canvas>` + `toBlob('image/jpeg')` decode
 *   that can't take HEIC/HEIF, and it fails SILENTLY — the raw undecoded file
 *   would upload labelled `image/jpeg` and render broken everywhere. See the
 *   branch comment below.
 * - user → all fields undefined so DropVideoStep keeps its video-only defaults.
 */

import { AddContentFlowType } from '@/domain/enums/publish/addContentFlowType';
import { MathUtils } from '@/domain/utils/mathUtils';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import {
  SUPPORTED_VIDEO_ACCEPT,
  SUPPORTED_IMAGE_ACCEPT,
  SUPPORTED_COMMUNITY_IMAGE_ACCEPT,
} from '@/domain/utils/videoFormatUtils';
import type { l10n } from '@/presentation/locale/l10n';

export interface DropStepConfig {
  /** `<input accept>` list; `undefined` → DropVideoStep's video-only default. */
  accept?: string;
  /** Drop-area heading; `undefined` → DropVideoStep's video-only default. */
  title?: string;
  /** Drop-area description; `undefined` → DropVideoStep's video-only default. */
  description?: string;
}

export function resolveDropStepConfig(flowType: AddContentFlowType, l10n: l10n): DropStepConfig {
  if (flowType === AddContentFlowType.community) {
    return {
      // Videos use the chat-equal full video accept list; the image half KEEPS
      // the narrower community list (no HEIC/HEIF → frozen ffmpeg image CF).
      accept: `${SUPPORTED_VIDEO_ACCEPT},${SUPPORTED_COMMUNITY_IMAGE_ACCEPT}`,
      title: l10n.publish.dropMediaHere,
      description: l10n.publish.dropMediaDescription.replace(
        '{max}',
        String(MathUtils.secondsToMinutesCeil(remoteConfigService.chatContentMaxSeconds)),
      ),
    };
  }

  if (flowType === AddContentFlowType.chat) {
    return {
      accept: `${SUPPORTED_VIDEO_ACCEPT},${SUPPORTED_IMAGE_ACCEPT}`,
      title: l10n.publish.dropMediaHere,
      description: l10n.publish.dropMediaDescription.replace(
        '{max}',
        String(MathUtils.secondsToMinutesCeil(remoteConfigService.chatContentMaxSeconds)),
      ),
    };
  }

  if (
    flowType === AddContentFlowType.feedback
    || flowType === AddContentFlowType.profileImage
  ) {
    // Feedback and profileImage are both image-only: NARROWER community image
    // accept list (no HEIC/HEIF) + image-drop copy. Identical config, shared
    // branch — the two flows reach the same narrow precondition by different
    // routes. Feedback: the constraint is the web read side's `<img>` (which
    // can't decode HEIC/HEIF on Chrome/Firefox), not a CF — feedback raw-uploads
    // the source file with no client-side re-encode (finding_1). ProfileImage:
    // it DOES re-encode, but through `memoryStorage.compressImage`, a `<canvas>`
    // + `canvas.toBlob('image/jpeg')` decode (memoryStorage.ts:85-136) that
    // Chrome/Firefox cannot feed a HEIC/HEIF source — and the failure is SILENT
    // (`compressImage` returns null, `downloadUserProfileImage` falls back to
    // `compressedBlob || imageBlob` at memoryStorage.ts:197), so the raw
    // undecoded HEIC would upload labelled `image/jpeg` and render broken for
    // every viewer on both clients. Its read side is an `<img>`/`CachedImage`
    // with the same constraint. Hence chat's FULL image list is deliberately not
    // reused here. Reuses the existing `dropImageTitle`/`dropImageDescription`
    // keys (already present, meaning "Drop image file here" / "Supported
    // formats: …").
    return {
      accept: SUPPORTED_COMMUNITY_IMAGE_ACCEPT,
      title: l10n.publish.dropImageTitle,
      description: l10n.publish.dropImageDescription,
    };
  }

  // User flow → DropVideoStep's video-only defaults (all undefined).
  return {};
}
